import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Mail, ArrowLeft, Twitter, Instagram, Facebook, Github } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ThemeLogo } from '../components/ui/theme-logo';

const Contact = () => {
  const location = useLocation();
  
  // Force scroll to top when this route is accessed
  useEffect(() => {
    // IMMEDIATE scroll - no delays
    const immediateScroll = () => {
      // Multi-method immediate scroll
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // Also scroll any potential scrollable containers immediately
      const scrollableElements = Array.from(document.querySelectorAll('*')).filter(el => {
        const style = getComputedStyle(el);
        return style.overflow === 'scroll' || style.overflow === 'auto' || style.overflowY === 'scroll' || style.overflowY === 'auto';
      });
      
      scrollableElements.forEach(el => {
        if (el.scrollTop > 0) {
          el.scrollTop = 0;
        }
      });
    };
    
    // Execute immediately
    immediateScroll();
    
    // Backup attempts with minimal delay
    requestAnimationFrame(immediateScroll);
    setTimeout(immediateScroll, 1);
    setTimeout(immediateScroll, 10);
    
    // One final check after very short delay to ensure React rendered
    setTimeout(immediateScroll, 50);
  }, [location.pathname]); // Trigger on route change



  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-white dark:from-background dark:to-gray-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-gradient-to-r from-background to-white dark:from-background dark:to-gray-950 shadow-md py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center">
              <ThemeLogo className="h-[42px]" />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/#features" className="text-gray-600 dark:text-gray-300 hover:text-primary transition-colors">Features</Link>
              <Link to="/#how-it-works" className="text-gray-600 dark:text-gray-300 hover:text-primary transition-colors">How It Works</Link>
              <Link to="/#community" className="text-gray-600 dark:text-gray-300 hover:text-primary transition-colors">Community</Link>
              <Link to="/#pricing" className="text-gray-600 dark:text-gray-300 hover:text-primary transition-colors">Pricing</Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/auth/login">
                <Button variant="ghost" className="hidden sm:flex">Sign in</Button>
              </Link>
              <Link to="/auth/login">
                <Button className="bg-primary hover:bg-primary-hover text-white">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Link to="/" className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-primary transition-colors mb-6">
              <ArrowLeft size={16} className="mr-2" />
              Back to Home
            </Link>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6"
            >
              Get in <span className="text-primary">Touch</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto"
            >
              Have questions about Nosh? We're here to help you reduce food waste and make meal planning easier.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700"
          >
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Contact Information</h2>
            
            <div className="flex justify-center">
              <div className="text-center max-w-sm">
                <div className="w-20 h-20 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <Mail className="h-10 w-10 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Email Support</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Have questions or need assistance? We're here to help!
                </p>
                <a href="mailto:hello@noshhq.com" className="text-lg text-primary hover:text-primary-hover transition-colors font-medium">
                  hello@noshhq.com
                </a>
              </div>
            </div>
          </motion.div>

          {/* Help Center Link */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-12 bg-primary/5 rounded-2xl p-8 border border-primary/20 text-center"
          >
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Need Quick Answers?</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-2xl mx-auto">
              Check out our comprehensive help center with frequently asked questions and detailed guides to get the most out of Nosh.
            </p>
            <Link to="/help-center">
              <Button size="lg" className="bg-primary hover:bg-primary-hover text-white px-8 py-3 rounded-xl">
                Visit Help Center
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <Link to="/" className="flex items-center mb-4">
                <img src="/nosh-new-wh.png" alt="Nosh" className="h-[42px]" />
              </Link>
              <p className="text-gray-400 mb-6 max-w-md">
                Reduce food waste with AI-powered inventory management, recipe suggestions, and meal tracking.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Twitter size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Instagram size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Facebook size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Github size={20} />
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><Link to="/#features" className="text-gray-400 hover:text-white transition-colors">Features</Link></li>
                <li><Link to="/#how-it-works" className="text-gray-400 hover:text-white transition-colors">How It Works</Link></li>
                <li><Link to="/#pricing" className="text-gray-400 hover:text-white transition-colors">Pricing</Link></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">API</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                <li><Link to="/help-center" className="text-gray-400 hover:text-white transition-colors">Help Center</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact Us</Link></li>
                <li><Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2025 Nosh. All rights reserved.
            </p>
            <div className="flex items-center mt-4 md:mt-0">
              <Mail size={16} className="mr-2 text-gray-400" />
              <a href="mailto:hello@noshhq.com" className="text-gray-400 hover:text-white transition-colors text-sm">
                hello@noshhq.com
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Contact;